import { randomUUID } from 'node:crypto'
import { resolveInside, type ToolApproval, type ToolGrant, type ToolGuard, type ToolRequest } from '@tnega/tools'
import { isLocalUrl, normalizeBrowserUrl } from '@tnega/browser'
import { BROWSER_OBSERVE_TOOLS } from '@tnega/tool-browser'
import type { ApprovalDecision } from '@tnega/approval-review'

export type PermissionMode = 'read-only' | 'workspace-write' | 'bypass'
export type PermissionModeSource = PermissionMode | (() => PermissionMode | Promise<PermissionMode>)

/**
 * 不需要逐次批准的调用：只读的工具，以及**只在 Project 内部发生**的动作。
 *
 * 后半类不碰这台机器：派工、线程间留言、给用户发言、写项目记忆与产物，都只改 Project
 * 自己的共享事实。它们造出来的 Agent 自己还要过同一道守卫，因此放行的半径由权限模型
 * 本身界定。真正需要用户点头的是越界与对外的动作 —— shell、工作区外的路径、网络。
 */
/** 与 `write_file` 同级：workspace-write 下在工作区内直接放行。 */
const WORKSPACE_WRITES = new Set(['write_file', 'edit_file', 'office_create', 'office_edit'])

/**
 * Tools whose `path` (`cwd` for shell) can leave the workspace under a `bypass`
 * parent; a narrower child must not reach outside through them unasked. Built
 * from the write list so a new write tool cannot be missed here.
 */
const PATH_TOOLS = new Set([...WORKSPACE_WRITES, 'read_file', 'list_dir', 'shell'])

const ALWAYS_ALLOWED = new Set([
  // 只读
  'now', 'read_file', 'list_dir',
  'glob', 'grep', 'http_get', 'web_search', 'web_fetch',
  // 用户自己的 Session 记录（各 Workspace），只读，不碰文件系统里的工作区内容。
  'search_sessions', 'search_session', 'read_session',
  // 插件开发模式的只读入口；plugin_write / plugin_remove 在宿主进程里执行代码，始终要批准。
  'plugin_list', 'plugin_read',
  'get_goal', 'update_goal', 'list_subagent', 'send_agent_message',
  'read_project', 'list_threads', 'read_artifact', 'office_inspect', 'office_read',
  // Project 内部
  'spawn_thread', 'send_thread_message', 'send_project_message', 'react_to_message', 'update_checklist', 'decide_thread_approval',
  'create_routine', 'list_routines', 'update_routine',
  'write_memory', 'publish_artifact', 'index_resource',
  // 编排入口不自行执行宿主操作，子工具仍经过同一道守卫。
  'run_code', 'ask_user_question',
  // 后台任务的读取与停止不碰宿主；启动走 shell 的 runInBackground，照样过 shell 那道守卫。
  'job_list', 'job_output', 'job_kill',
])

/** Whether a `skill` call writes (creates or installs) rather than reads. */
function skillActionWrites(input: Record<string, unknown>): boolean {
  return input.action === 'create' || input.action === 'install'
}

/**
 * Browser tools that only move around or look: navigation is a GET like
 * `http_get`, and these never submit anything on their own.
 */
const BROWSER_PASSIVE = new Set([
  ...BROWSER_OBSERVE_TOOLS,
  'browser_navigate', 'browser_navigate_back', 'browser_reload',
  'browser_scroll', 'browser_hover', 'browser_wait_for', 'browser_resize', 'browser_tabs',
])

/**
 * Clicking, typing and running code on a local development page is ordinary
 * frontend work, at the same tier as editing workspace files. The same
 * interactions on any other site can submit forms or spend money, so they ask.
 */
function browserInteractionIsLocal(request: ToolRequest, pageUrl: string | undefined): boolean {
  if (!request.name.startsWith('browser_')) return false
  return pageUrl !== undefined && isLocalUrl(normalizeBrowserUrl(pageUrl))
}

/** The Session-long scope a tool offers for this call, if any; a failing lookup offers none. */
async function grantOf(request: ToolRequest): Promise<ToolGrant | undefined> {
  if (!request.tool.grant) return undefined
  try {
    return await request.tool.grant(request.input, request.options)
  } catch {
    return undefined
  }
}

/**
 * What the tool itself asks for this call. A failing lookup asks a person:
 * the tool meant to say something about this call and could not.
 */
async function approvalOf(request: ToolRequest): Promise<ToolApproval | undefined> {
  if (!request.tool.approval) return undefined
  try {
    return await request.tool.approval(request.input, request.options)
  } catch (error) {
    return { need: 'person', reason: `could not check whether this call is safe (${error instanceof Error ? error.message : String(error)})` }
  }
}

/** How an approval request ended. */
export type ApprovalOutcome = 'allow' | 'deny' | 'timeout' | 'cancelled'

/** How long a person has to answer an approval request. */
export const APPROVAL_TIMEOUT_MS = 120_000

interface PendingApproval {
  key: string
  resolve: (outcome: ApprovalOutcome) => void
  timer: ReturnType<typeof setTimeout>
  signal?: AbortSignal
  onAbort: () => void
}

export class ApprovalBroker {
  private readonly runs = new Map<string, (event: Record<string, unknown>) => void>()
  private readonly pending = new Map<string, PendingApproval>()
  /** Scopes a person allowed for the rest of a Session, by run key. */
  private readonly grants = new Map<string, Set<string>>()

  /** Whether a person already allowed `scope` in this Session. */
  granted(key: string, scope: string): boolean {
    return this.grants.get(key)?.has(scope) === true
  }

  /** Remember that a person allowed `scope` for the rest of this Session. */
  grant(key: string, scope: string): void {
    let scopes = this.grants.get(key)
    if (!scopes) {
      scopes = new Set()
      this.grants.set(key, scopes)
    }
    scopes.add(scope)
  }

  attach(key: string, emit: (event: Record<string, unknown>) => void): () => void {
    this.runs.set(key, emit)
    return () => {
      this.runs.delete(key)
      for (const [id, approval] of this.pending) {
        if (approval.key === key) this.settle(id, 'cancelled')
      }
    }
  }

  decide(id: string, key: string, allow: boolean): boolean {
    if (this.pending.get(id)?.key !== key) return false
    this.settle(id, allow ? 'allow' : 'deny')
    return true
  }

  /** Whether a person still has to answer for this run before it can go on. */
  pendingFor(key: string): boolean {
    for (const approval of this.pending.values()) {
      if (approval.key === key) return true
    }
    return false
  }

  async request(key: string, request: ToolRequest, options: { fullInput?: boolean } = {}): Promise<boolean> {
    return await this.ask(key, request, options) === 'allow'
  }

  /**
   * Ask a person and say how it ended: answered, unanswered in time, or
   * cancelled. With `grant`, the card offers that scope for the rest of the
   * Session, and an allow is remembered for it.
   */
  ask(key: string, request: ToolRequest, options: { fullInput?: boolean; grant?: ToolGrant; reason?: string } = {}): Promise<ApprovalOutcome> {
    const emit = this.runs.get(key)
    if (!emit || request.options.signal?.aborted) return Promise.resolve('cancelled')
    const id = randomUUID()
    return new Promise(resolve => {
      const signal = request.options.signal
      const onAbort = (): void => this.settle(id, 'cancelled')
      const timer = setTimeout(() => this.settle(id, 'timeout'), APPROVAL_TIMEOUT_MS)
      this.pending.set(id, {
        key, resolve, timer, onAbort, ...(signal ? { signal } : {}),
      })
      signal?.addEventListener('abort', onAbort, { once: true })
      try {
        emit({
          type: 'approval/request',
          id,
          tool: request.name,
          input: options.fullInput ? (JSON.stringify(request.input) ?? '') : (JSON.stringify(request.input) ?? '').slice(0, 2_000),
          // A call a CodeMode script made: the model only called run_code.
          ...(typeof request.options.ptcParentCallId === 'string' ? { via: 'run_code' } : {}),
          ...(options.grant ? { grant: { scope: options.grant.scope, label: options.grant.label } } : {}),
          ...(options.reason ? { reason: options.reason } : {}),
        })
      } catch {
        this.settle(id, 'cancelled')
      }
    })
  }

  private settle(id: string, outcome: ApprovalOutcome): void {
    const approval = this.pending.get(id)
    if (!approval) return
    this.pending.delete(id)
    clearTimeout(approval.timer)
    approval.signal?.removeEventListener('abort', approval.onAbort)
    approval.resolve(outcome)
  }
}

export function permissionGuard(
  mode: PermissionModeSource,
  key: string,
  approvals: ApprovalBroker,
  options: {
    workspace: string
    agentMode?: (agentId: string) => PermissionMode | undefined
    review?: (request: ToolRequest) => Promise<ApprovalDecision | undefined>
    delegateApproval?: (request: ToolRequest, review: ApprovalDecision | undefined) => Promise<ApprovalDecision>
    /** URL of the agent browser's current page, for the browser tool rules. */
    browserUrl?: () => string | undefined
    /**
     * Agents whose approvals never reach a person: project threads. The
     * automatic reviewer decides first. An undecided call is delegated to its
     * direct parent; explicit reviewer denials remain terminal.
     */
    delegated?: (agentId: string | undefined) => boolean
  },
): ToolGuard {
  return async request => {
    const parentMode = typeof mode === 'function' ? await mode() : mode
    const childMode = request.options.agentId ? options.agentMode?.(request.options.agentId) : undefined
    const rank = { 'read-only': 0, 'workspace-write': 1, bypass: 2 }
    const effective = childMode && rank[childMode] < rank[parentMode] ? childMode : parentMode
    if (effective === 'bypass') return undefined
    const unrestricted = parentMode === 'bypass'
    const pathKey = request.name === 'shell' ? 'cwd' : 'path'
    const input = request.input && typeof request.input === 'object' && !Array.isArray(request.input)
      ? request.input as Record<string, unknown> : {}
    const rawPath = typeof input[pathKey] === 'string' ? input[pathKey] : '.'
    let scoped = true
    if (unrestricted && PATH_TOOLS.has(request.name)) {
      try { await resolveInside(options.workspace, rawPath) } catch { scoped = false }
    }
    // One tool, several verbs: reading skills never needs approval, writing
    // one into the user's home directory is the same tier as a workspace write.
    if (request.name === 'skill' && !skillActionWrites(input)) return undefined
    if (ALWAYS_ALLOWED.has(request.name) && scoped
      && !(request.name === 'http_get' && unrestricted)) return undefined
    if (effective === 'workspace-write' && request.name === 'skill' && skillActionWrites(input)) return undefined
    if (effective === 'workspace-write' && WORKSPACE_WRITES.has(request.name) && scoped) return undefined
    if (BROWSER_PASSIVE.has(request.name)) return undefined
    if (effective === 'workspace-write' && browserInteractionIsLocal(request, options.browserUrl?.())) return undefined
    if (request.options.signal?.aborted) return 'Tool approval cancelled'
    const delegated = options.delegated?.(request.options.agentId) === true
    const own = await approvalOf(request)
    if (own?.need === 'none' && effective === 'workspace-write') return undefined
    // The tool says a person must decide this call: no grant, reviewer or
    // delegate answers for them. An agent with no person to ask cannot run it.
    if (own?.need === 'person') {
      if (!delegated) return await askPerson(request, own.reason, parentMode)
      return `${request.name} needs the user's own approval (${own.reason}), which cannot be asked from here. Do not retry it; report it to your coordinator so the user can do or approve it.`
    }
    const grant = await grantOf(request)
    if (grant && approvals.granted(key, grant.scope)) return undefined
    let reviewed: ApprovalDecision | undefined
    // A narrower child cannot use the parent's automatic elevation policy, unless
    // the reviewer is the only one who decides for it (a project thread).
    if (delegated || !(childMode && rank[childMode] < rank[parentMode])) {
      try { reviewed = await options.review?.(request) } catch { /* Fall back to human approval. */ }
    }
    if (request.options.signal?.aborted) return 'Tool approval cancelled'
    if (typeof mode === 'function' && await mode() !== parentMode) return 'Tool permission changed during review'
    if (delegated && reviewed?.decision !== 'allow' && reviewed?.decision !== 'deny' && options.delegateApproval) {
      reviewed = await options.delegateApproval(request, reviewed)
      if (request.options.signal?.aborted) return 'Tool approval cancelled'
      if (typeof mode === 'function' && await mode() !== parentMode) return 'Tool permission changed during approval'
    }
    if (delegated && reviewed?.decision !== 'allow') {
      const reason = reviewed?.reason ? `: ${reviewed.reason}` : ''
      if (options.delegateApproval) return `${request.name} was not approved${reason}. This call is finished; do not retry it unchanged. Find another way within the rules or report the remaining blocker to your coordinator.`
      return `${request.name} was not approved${reason}. Do not retry it unchanged. If it is necessary, ask your coordinator with send_thread_message (kind request), saying what you need and why; it decides, or asks the user when the call is theirs.`
    }
    if (delegated) {
      request.options.approvedElevation = true
      return undefined
    }
    if (reviewed?.decision === 'deny') return `Automatic review denied ${request.name}: ${reviewed.reason}`
    const asked = reviewed?.decision !== 'allow'
    const outcome = asked ? await approvals.ask(key, request, grant ? { grant } : {}) : 'allow'
    if (request.options.signal?.aborted) return 'Tool approval cancelled'
    if (typeof mode === 'function' && await mode() !== parentMode) return 'Tool permission changed during approval'
    // Only a person's answer covers the scope; an automatic allow stays per call.
    if (outcome === 'allow' && asked && grant) approvals.grant(key, grant.scope)
    if (outcome === 'allow') {
      request.options.approvedElevation = true
      return undefined
    }
    if (outcome === 'timeout') return unanswered(request)
    return `${request.name} requires human approval in ${effective} mode`
  }

  async function askPerson(request: ToolRequest, reason: string, parentMode: PermissionMode): Promise<string | undefined> {
    const outcome = await approvals.ask(key, request, { reason })
    if (request.options.signal?.aborted) return 'Tool approval cancelled'
    if (typeof mode === 'function' && await mode() !== parentMode) return 'Tool permission changed during approval'
    if (outcome === 'allow') {
      request.options.approvedElevation = true
      return undefined
    }
    if (outcome === 'timeout') return unanswered(request)
    return `The user did not allow ${request.name} (${reason}). Do not retry it; find another way, or ask the user in your reply.`
  }
}

function unanswered(request: ToolRequest): string {
  return `${request.name} was not approved: nobody answered the approval request within ${APPROVAL_TIMEOUT_MS / 60_000} minutes. Do not retry it now; if it is still needed, say so in your reply so the user can approve it.`
}
